import { useEffect, useMemo, useState } from 'react';
import { Colors, Text } from '@mercator-gcp/grout';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import { Pagination } from 'components/layout/List/Paging';
import PastProjects from 'components/widgets/ProjectDetails/PastProjects';
import { useProjectsByBbox } from 'hooks/useProjectsByBbox';
import { ProjectGroupTypes } from 'utils/analytics/constants';

type GeographicPoint = {
  lat: number;
  lng: number;
};

type Props = {
  identifier: string | number;
  projectSlug: string;
  projectAddress: string;
  projectParcelPolygon?: GeographicPoint[] | null;
  isLoading?: boolean;
};

const ProjectsInParcelSection = ({
  projectSlug,
  projectAddress,
  projectParcelPolygon,
  isLoading = false,
  identifier,
}: Props) => {
  const PAGE_SIZE = 5;
  const [parcelPolygonBbox, setParcelPolygonBbox] = useState<string | null>();
  const {
    projects: projectsList,
    isLoading: isBboxProjectsLoading,
    count,
    setPage,
    page,
  } = useProjectsByBbox({ bbox: parcelPolygonBbox });

  const isSomethingLoading = useMemo(() => {
    return isLoading || isBboxProjectsLoading;
  }, [isLoading, isBboxProjectsLoading]);

  const totalPages = (): number => {
    return count ? Math.ceil(count / PAGE_SIZE) : 1;
  };

  const filterProjectList = useMemo(() => {
    if (projectsList && projectsList.length > 0) {
      // only show projects that are not the current project and not the same address
      return projectsList.filter(
        (item) => item.slug !== projectSlug && item.address !== projectAddress,
      );
    }

    return [];
  }, [projectSlug, projectAddress, projectsList]);

  useEffect(() => {
    if (!projectParcelPolygon) {
      setParcelPolygonBbox(null);
    } else {
      // Convert the list of lat/lngs into the bbox string format
      const formattedPairs = projectParcelPolygon.map(
        (coord) => `${coord.lng} ${coord.lat}`,
      );
      const bboxFormattedPolygon = formattedPairs.join(', ');
      setParcelPolygonBbox(bboxFormattedPolygon);
    }
  }, [setParcelPolygonBbox, projectParcelPolygon]);

  const paginationContainer = {
    mb: 'padding_48',
    mt: 'padding_24',
  };

  const containerStyle = {
    width: '100%',
  };

  const labelStyle = {
    color: Colors.TextQuiet,
  };
  if (filterProjectList && filterProjectList.length === 0 && !isSomethingLoading) {
    return (
      <FlexColumn sx={containerStyle}>
        <PastProjects
          projectsList={filterProjectList}
          loading={isSomethingLoading}
          mapLoading={isSomethingLoading}
          leadContext={{
            groupId: identifier.toString(),
            groupType: ProjectGroupTypes.OTHER_PROJECTS,
          }}
        >
          Other Projects at This Parcel
        </PastProjects>
        <Text.Body sx={labelStyle}>No other projects found at this parcel</Text.Body>
      </FlexColumn>
    );
  }

  return (
    <FlexColumn sx={containerStyle}>
      <PastProjects
        projectsList={filterProjectList}
        loading={isSomethingLoading}
        mapLoading={isSomethingLoading}
        leadContext={{
          groupId: identifier.toString(),
          groupType: ProjectGroupTypes.OTHER_PROJECTS,
        }}
        keyPrefix="same-parcel"
      >
        Other Projects at This Parcel
      </PastProjects>
      {totalPages && totalPages() > 1 && (
        <FlexRow sx={paginationContainer}>
          <Pagination
            currentPage={page}
            isLoading={isSomethingLoading}
            setPage={(pageNumber) => setPage(pageNumber)}
            totalPages={totalPages()}
            onNext={() => setPage(page + 1)}
            onPrev={() => setPage(page - 1)}
          />
        </FlexRow>
      )}
    </FlexColumn>
  );
};

export default ProjectsInParcelSection;
